import { UseFormReturn } from "react-hook-form";
import { createStore } from "zustand";

import { SessionInferenceMode } from "../../graphql/schema.graphql";
import { ReferenceImageType } from "../../ui-v2/References/types";
import {
  FormTypeCanvasCreate2D,
  FormTypeCanvasPromptEdit,
} from "../forms/Canvas/types";

export type CanvasInferenceMode = "CREATE" | "PROMPT_EDIT";

/**
 * Note: Many fields in this store are typed as `| null` or `| undefined` to match
 * how `defaultProps` initializes the store. Once we review the initialization logic,
 * we should revisit these nullable types and narrow them where appropriate.
 */
interface InferenceFormInternalProps {
  activeMode: SessionInferenceMode | null;
  canvasMode: CanvasInferenceMode;
  assetId?: string | null;
  refinementAssetsDirty: boolean;
  refinementImages: ReferenceImageType[];
  inferenceFormCanvasCreate2D: UseFormReturn<FormTypeCanvasCreate2D> | null;
  inferenceFormCanvasPromptEdit: UseFormReturn<FormTypeCanvasPromptEdit> | null;
}

const createDefaultProps = (): InferenceFormInternalProps => ({
  activeMode: null,
  canvasMode: "CREATE",
  assetId: null,
  refinementAssetsDirty: false,
  refinementImages: [],
  inferenceFormCanvasCreate2D: null,
  inferenceFormCanvasPromptEdit: null,
});

export type AnyFormReturn =
  | UseFormReturn<FormTypeCanvasCreate2D>
  | UseFormReturn<FormTypeCanvasPromptEdit>;

export interface InferenceFormState extends InferenceFormInternalProps {
  setActiveMode: (mode: SessionInferenceMode) => void;
  setCanvasMode: (mode: CanvasInferenceMode) => void;
  setAssetId: (assetId?: string) => void;
  setRefinementImages: (
    images: ReferenceImageType[],
    opts?: { markDirty?: boolean },
  ) => void;
  setInferenceFormCanvasCreate2D: (
    form: UseFormReturn<FormTypeCanvasCreate2D>,
  ) => void;
  setInferenceFormCanvasPromptEdit: (
    form: UseFormReturn<FormTypeCanvasPromptEdit>,
  ) => void;

  resetStore: () => void;
  activeForm?: AnyFormReturn;
}

const getActiveForm = (
  state: InferenceFormInternalProps,
): AnyFormReturn | undefined => {
  switch (state.activeMode) {
    case "CANVAS_2_D":
      return state.inferenceFormCanvasCreate2D ?? undefined;
    case "CANVAS_PROMPT_EDIT":
      return state.inferenceFormCanvasPromptEdit ?? undefined;
    default:
      return undefined;
  }
};

export const inferenceFormStore = () => {
  const initProps = createDefaultProps();

  return createStore<InferenceFormState>()((set) => ({
    ...initProps,
    activeForm: getActiveForm(initProps),
    setActiveMode: (activeMode) =>
      set((state) => ({
        activeMode,
        activeForm: getActiveForm({ ...state, activeMode }),
      })),
    setCanvasMode: (canvasMode) =>
      set(() => ({
        canvasMode,
      })),
    setAssetId: (assetId) => set(() => ({ assetId })),
    setRefinementImages: (refinementImages, { markDirty = true } = {}) =>
      set(() => ({ refinementImages, refinementAssetsDirty: markDirty })),
    setInferenceFormCanvasCreate2D: (inferenceFormCanvasCreate2D) =>
      set(() => ({ inferenceFormCanvasCreate2D })),
    setInferenceFormCanvasPromptEdit: (inferenceFormCanvasPromptEdit) =>
      set(() => ({ inferenceFormCanvasPromptEdit })),
    resetStore: () =>
      set(() => {
        const resetState = { ...createDefaultProps() };
        return {
          ...resetState,
          activeForm: getActiveForm(resetState),
        };
      }),
  }));
};

export type InferenceFormStore = ReturnType<typeof inferenceFormStore>;
